/* eslint-disable react/no-array-index-key */
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import { Collapse, DialogContent } from '@material-ui/core';
import Dialog from '@comp/Dialog/Dialog';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import { DARK_THEME, Spacings } from '@styles/index';
import { useContext } from 'react';
import { QUESTION_TYPE_MAP } from '@constants/quiz';

const Modal = styled(Dialog)`
  &.MuiDialog-root {
    .MuiDialog-container {
      .MuiPaper-root {
        height: 600px;
        width: 800px;
        background-color: ${({ theme }) => theme.colors.notfnHover};
        .MuiDialogContent-root {
          padding-top: 24px;
        }
      }
    }
  }
`;

const Fields = styled.div`
  width: 700px;
  margin: auto;
  border-radius: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_6B};
  background-color: ${({ theme }) => theme.colors.whiteV3};
`;

const Answer = styled(H5)`
  margin-right: ${Spacings.SPACING_4B};
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const Area = styled.div`
  min-height: ${({ minHeight }) => minHeight || '150px'};
  margin: auto;
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  overflow: hidden;
`;

const OptionsArea = styled.div`
  min-height: ${({ minHeight }) => minHeight || '150px'};
  width: 48%;
  border-radius: ${Spacings.SPACING_2B};
  border: ${Spacings.SPACING_1} solid
    ${({ theme }) => theme.colors.fontLightBlue};
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  overflow: hidden;
`;

const Options = styled.div`
  margin: auto;
  padding: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_4B};
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  overflow: hidden;
`;

const Text = styled(P1)`
  u {
    text-decoration: underline;
  }
  img {
    cursor: inherit;
    max-width: 100%;
  }
  img[data-latex='true'] {
    cursor: inherit;
    max-width: 100%;
    ${({ theme }) => theme.colors.type === DARK_THEME && 'filter: invert(1);'}
  }
`;

const ReadMore = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  padding: ${Spacings.SPACING_3B} 0;
  background: ${({ theme }) => theme.colors.dropShadowV2};
  border: ${({ theme }) => `1px solid ${theme.colors.dropShadowV4}`};
  border-top-left-radius: ${Spacings.SPACING_2B};
  border-top-right-radius: ${Spacings.SPACING_2B};
`;

const PassageWrapper = styled.div`
  padding: ${Spacings.SPACING_6B};
  border: ${({ theme }) => `1px solid ${theme.colors.dropShadowV4}`};
  height: auto;
  overflow: scroll;
  position: relative;
  border-bottom-left-radius: ${Spacings.SPACING_2B};
  border-bottom-right-radius: ${Spacings.SPACING_2B};
`;

const Heading = styled(H5)`
  color: ${({ theme }) => theme.colors.fontGrey};
`;

const QuestionWebPreview = ({
  data,
  selectedLanguage,
  showWebPreview,
  onClose
}) => {
  const {
    question,
    questionType,
    singleSolution,
    upperLimit,
    lowerLimit,
    isIntegerRange,
    hasPassage,
    passage,
    options,
    solutionExplanation,
    hint
  } = data;
  const selectedTheme = useContext(ThemeContext);

  return (
    <Modal dialogState={showWebPreview} onClose={onClose} center>
      <DialogContent>
        <Fields>
          <Collapse in={hasPassage} timeout="auto" unmountOnExit>
            <Area>
              <ReadMore>
                <Heading>Passage</Heading>
              </ReadMore>
              <PassageWrapper>
                <Text
                  color={selectedTheme.colors.fontGrey}
                  dangerouslySetInnerHTML={{
                    __html: passage[selectedLanguage]
                  }}
                />
              </PassageWrapper>
            </Area>
          </Collapse>
          <Area>
            <Heading>Question 15</Heading>
            <Text
              color={selectedTheme.colors.fontGrey}
              dangerouslySetInnerHTML={{
                __html: question[selectedLanguage]
              }}
            />
          </Area>
          {questionType === QUESTION_TYPE_MAP.INTEGER ? (
            <>
              <Area minHeight="66px">
                <Heading>Answer</Heading>
                <Text
                  color={selectedTheme.colors.fontGrey}
                  dangerouslySetInnerHTML={{
                    __html: singleSolution?.[selectedLanguage]
                  }}
                />
              </Area>
              <Collapse in={isIntegerRange} timeout="auto" unmountOnExit>
                <Area minHeight="66px">
                  <Heading>Lower limit</Heading>
                  <Text
                    color={selectedTheme.colors.fontGrey}
                    dangerouslySetInnerHTML={{
                      __html: lowerLimit?.[selectedLanguage]
                    }}
                  />
                </Area>
                <Area minHeight="66px">
                  <Heading>Upper limit</Heading>
                  <Text
                    color={selectedTheme.colors.fontGrey}
                    dangerouslySetInnerHTML={{
                      __html: upperLimit?.[selectedLanguage]
                    }}
                  />
                </Area>
              </Collapse>
            </>
          ) : (
            <Options>
              {options.map((option, index) => (
                <OptionsArea
                  minHeight="66px"
                  key={`${selectedLanguage}-${index}`}
                >
                  <span>
                    <Answer>
                      {String.fromCharCode('A'.charCodeAt() + index)}
                    </Answer>
                  </span>
                  <Text
                    color={selectedTheme.colors.fontGrey}
                    dangerouslySetInnerHTML={{
                      __html: option[selectedLanguage]
                    }}
                  />
                </OptionsArea>
              ))}
            </Options>
          )}
        </Fields>
        <Fields>
          <Area>
            <Heading>Solution</Heading>
            <Text
              color={selectedTheme.colors.fontGrey}
              dangerouslySetInnerHTML={{
                __html: solutionExplanation[selectedLanguage]
              }}
            />
          </Area>
        </Fields>
        <Fields>
          <Area>
            <Heading>Hint</Heading>
            <Text
              color={selectedTheme.colors.fontGrey}
              dangerouslySetInnerHTML={{ __html: hint[selectedLanguage] }}
            />
          </Area>
        </Fields>
      </DialogContent>
    </Modal>
  );
};

export default QuestionWebPreview;

QuestionWebPreview.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  selectedLanguage: PropTypes.number,
  showWebPreview: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

QuestionWebPreview.defaultProps = {
  data: {},
  selectedLanguage: -1
};
